import { useNotification } from '../components/Notification/NotificationSystem';

// Enhanced notification hook with common use cases
export const useNotificationHook = () => {
  const notification = useNotification();

  // Common notification patterns
  const notify = {
    // Success notifications
    success: (message, title = 'Success', options = {}) => {
      notification.showSuccess(message, title, {
        duration: 4000,
        ...options,
      });
    },

    // Error notifications
    error: (message, title = 'Error', options = {}) => {
      notification.showError(message, title, {
        duration: 6000,
        ...options,
      });
    },

    // Warning notifications
    warning: (message, title = 'Warning', options = {}) => {
      notification.showWarning(message, title, {
        duration: 5000,
        ...options,
      });
    },

    // Info notifications
    info: (message, title = 'Information', options = {}) => {
      notification.showInfo(message, title, {
        duration: 3000,
        ...options,
      });
    },

    // Form validation errors
    validationError: (errors) => {
      const errorMessages = Array.isArray(errors) 
        ? errors.join(', ') 
        : Object.values(errors).join(', ');
      
      notification.showError(
        errorMessages,
        'Validation Error',
        {
          duration: 5000,
          actions: [
            {
              label: 'Fix Issues',
              type: 'primary',
              onClick: () => {
                // Scroll to first error field
                const firstError = document.querySelector('.error, [data-error="true"]');
                if (firstError) {
                  firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }
            }
          ]
        }
      );
    },

    // API response notifications
    apiSuccess: (message, title = 'Success') => {
      notification.showSuccess(message, title, {
        duration: 3000,
        actions: [
          {
            label: 'View Details',
            type: 'primary',
            onClick: () => console.log('View details clicked')
          }
        ]
      });
    },

    apiError: (error, title = 'API Error') => {
      const message = error?.message || error?.response?.data?.message || 'An error occurred';
      notification.showError(message, title, {
        duration: 6000,
        actions: [
          {
            label: 'Retry',
            type: 'primary',
            onClick: () => window.location.reload()
          },
          {
            label: 'Report Issue',
            type: 'secondary',
            onClick: () => {
              // Open contact form or support page
              window.open('/contact', '_blank');
            }
          }
        ]
      });
    },

    // Authentication notifications
    loginSuccess: () => {
      notification.showSuccess(
        'Welcome back! You have been successfully logged in.',
        'Login Successful',
        {
          duration: 3000,
          actions: [
            {
              label: 'Go to Dashboard',
              type: 'primary',
              onClick: () => window.location.href = '/dashboard'
            }
          ]
        }
      );
    },

    loginError: (message = 'Invalid credentials. Please try again.') => {
      notification.showError(message, 'Login Failed', {
        duration: 5000,
        actions: [
          {
            label: 'Try Again',
            type: 'primary',
            onClick: () => {
              // Focus on login form
              const loginForm = document.querySelector('form[data-login="true"]');
              if (loginForm) {
                const emailInput = loginForm.querySelector('input[type="email"]');
                if (emailInput) emailInput.focus();
              }
            }
          }
        ]
      });
    },

    logoutSuccess: () => {
      notification.showInfo(
        'You have been successfully logged out.',
        'Logout Successful',
        {
          duration: 3000
        }
      );
    },

    // Payment notifications
    paymentSuccess: (amount, orderId) => {
      notification.showSuccess(
        `Payment of $${amount} processed successfully! Order #${orderId}`,
        'Payment Successful',
        {
          duration: 5000,
          actions: [
            {
              label: 'View Order',
              type: 'primary',
              onClick: () => window.location.href = `/orders/${orderId}`
            },
            {
              label: 'Download Receipt',
              type: 'secondary',
              onClick: () => {
                // Trigger receipt download
                console.log('Download receipt');
              }
            }
          ]
        }
      );
    },

    paymentError: (message = 'Payment processing failed. Please try again.') => {
      notification.showError(message, 'Payment Failed', {
        duration: 6000,
        actions: [
          {
            label: 'Try Again',
            type: 'primary',
            onClick: () => window.location.reload()
          },
          {
            label: 'Contact Support',
            type: 'secondary',
            onClick: () => window.open('/contact', '_blank')
          }
        ]
      });
    },

    // File upload notifications
    uploadSuccess: (filename) => {
      notification.showSuccess(
        `File "${filename}" uploaded successfully!`,
        'Upload Complete',
        {
          duration: 4000
        }
      );
    },

    uploadError: (filename, error) => {
      notification.showError(
        `Failed to upload "${filename}". ${error}`,
        'Upload Failed',
        {
          duration: 5000,
          actions: [
            {
              label: 'Try Again',
              type: 'primary',
              onClick: () => {
                // Trigger file input
                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) fileInput.click();
              }
            }
          ]
        }
      );
    },

    // Form notifications
    formSaved: (formName = 'form') => {
      notification.showSuccess(
        `${formName} saved successfully!`,
        'Save Successful',
        {
          duration: 3000
        }
      );
    },

    formError: (formName = 'form') => {
      notification.showError(
        `Failed to save ${formName}. Please try again.`,
        'Save Failed',
        {
          duration: 5000,
          actions: [
            {
              label: 'Retry',
              type: 'primary',
              onClick: () => {
                // Trigger form submission
                const form = document.querySelector('form');
                if (form) form.submit();
              }
            }
          ]
        }
      );
    },

    // Network notifications
    networkError: () => {
      notification.showError(
        'Network connection lost. Please check your internet connection.',
        'Connection Error',
        {
          duration: 0, // Persistent until user dismisses
          actions: [
            {
              label: 'Retry',
              type: 'primary',
              onClick: () => window.location.reload()
            },
            {
              label: 'Dismiss',
              type: 'secondary',
              onClick: () => console.log('Dismissed')
            }
          ]
        }
      );
    },

    // Maintenance notifications
    maintenanceMode: () => {
      notification.showWarning(
        'The system is currently under maintenance. Some features may be unavailable.',
        'Maintenance Mode',
        {
          duration: 0, // Persistent
          actions: [
            {
              label: 'Learn More',
              type: 'primary',
              onClick: () => window.open('/maintenance', '_blank')
            }
          ]
        }
      );
    },

    // Custom notification with full control
    custom: (type, message, title, options = {}) => {
      switch (type) {
        case 'success':
          notification.showSuccess(message, title, options);
          break;
        case 'error':
          notification.showError(message, title, options);
          break;
        case 'warning':
          notification.showWarning(message, title, options);
          break;
        case 'info':
          notification.showInfo(message, title, options);
          break;
        default:
          notification.showInfo(message, title, options);
      }
    }
  };

  return {
    ...notification,
    notify
  };
}; 